<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col lg:flex-row w-full gap-4 items-start lg:items-center">
      <div class="flex flex-wrap items-center gap-4 flex-shrink-0 w-full lg:w-auto">
        <div class="flex items-center gap-2 flex-1">
          <h1 class="text-text-primary text-xl font-medium">{{ $t('coach.shared.match_history') }}</h1>
          <svg v-if="gettingMatches" width="16" height="16" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" class="animate-spin">
            <circle cx="16" cy="16" r="15" stroke="#1E2733" stroke-width="2" />
            <path d="M16 1C17.9698 1 19.9204 1.38799 21.7403 2.14181C23.5601 2.89563 25.2137 4.00052 26.6066 5.3934C27.9995 6.78628 29.1044 8.43986 29.8582 10.2597C30.612 12.0796 31 14.0302 31 16" stroke="#4E54C8" stroke-width="2" />
          </svg>
          <HandThumbUpIcon v-if="gettingMatchesSuccess" class="h-4 w-4 text-text-primary" />
        </div>
        <button
          @click="retrieveMatches()"
          class="w-full sm:w-auto bg-gray-800 rounded-lg py-2 px-3 text-text-primary text-xs hover:bg-gray-700 duration-300"
        >
          {{ $t('coach.match_history.get_recent') }}
        </button>
      </div>
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full lg:w-auto lg:ml-auto">
        <CoachFilters
          horizontal
          :platform="selectedPlatform"
          :matchType="selectedMatchType"
          :map="selectedMap"
          :maps="playerMaps"
          @update:platform="onPlatformChange"
          @update:matchType="onMatchTypeChange"
          @update:map="onMapChange"
        />
      </div>
    </div>

    <div class="w-full flex flex-col gap-[2px] rounded-lg overflow-hidden">
      <!-- Header with pagination -->
      <div
        v-if="!loadingMatches && matchHistory?.length > 0"
        class="bg-gray-900 text-text-primary text-xs tracking-[1.5px] flex-1 uppercase flex px-4 py-2 md:gap-2 items-center"
      >
        <div class="flex-1 md:flex-none">{{ $t('coach.match_history.last_n_matches', { n: matchHistory.length }) }}</div>
        <span class="hidden md:block text-text-secondary">&bull;</span>
        <span
          class="hidden md:block"
          :class="{
            'text-success-400': recentWinRate > 50,
            'text-text-primary': recentWinRate == 50,
            'text-danger-400': recentWinRate < 50,
          }"
        >{{ recentWinRate }}%</span>
        <div class="flex-1 flex items-center">
          <h1 class="flex-1 hidden md:block">{{ $t('coach.match_history.win_ratio') }}</h1>
          <Pagination
            :currentPage="currentPage"
            :totalPages="totalPageCount"
            :showCount="false"
            @changePage="changePage"
          />
        </div>
      </div>

      <!-- Match rows -->
      <template v-if="matchHistory?.length > 0">
        <div v-for="match in matchHistory" :key="match.match.uuid" class="bg-gray-900">
          <MatchRow :match="match" />
        </div>
      </template>

      <MatchSectionSkeleton v-else-if="loadingMatches" :rows="5" />

      <i18n-t scope="global" v-else keypath="coach.match_history.none_found" tag="div" class="bg-gray-900 p-4 text-text-secondary text-xs tracking-[1.5px]">
        <template #link><router-link to="/settings" class="underline">{{ $t('coach.match_history.none_found_link') }}</router-link></template>
      </i18n-t>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useStore } from 'vuex';
import { HandThumbUpIcon } from '@heroicons/vue/24/solid';
import axios from 'axios';
import {
  retrievalStartedMessage,
  retrievalFailedMessage,
  messageDismissDelay,
  SHORT_DISMISS_MS,
} from '../../../../utils/match_retrieval';

import MatchRow from './MatchRow.vue';
import MatchSectionSkeleton from './MatchSectionSkeleton.vue';
import CoachFilters from '../../sections/CoachFilters.vue';
import Pagination from '../../../Pagination.vue';
import { platformFilterToApi } from '../../../../utils/matchPlatform.js';
import { MAP_NAMES } from '../../../../composables/useCoachData.js';

const auth = useAuth();
const user = ref(auth.user());
const route = useRoute();
const router = useRouter();
const store = useStore();

const matchHistory = ref([]);
const loadingMatches = ref(true);
const gettingMatches = ref(false);
const gettingMatchesSuccess = ref(false);
const selectedPlatform = ref('');
const selectedMatchType = ref('');
const selectedMap = ref(null);
const currentPage = ref(1);
const totalPageCount = ref(1);

// Derive map list from match history results
const playerMaps = computed(() => {
  if (!matchHistory.value?.length) return [];
  const mapSet = new Map();
  matchHistory.value.forEach((entry) => {
    const name = entry.match.map_name;
    if (name && !mapSet.has(name)) {
      mapSet.set(name, {
        name,
        friendly_name: MAP_NAMES[name] || name.replace('de_', '').replace(/^\w/, (c) => c.toUpperCase()),
      });
    }
  });
  return Array.from(mapSet.values());
});

const recentWinRate = computed(() => {
  if (!matchHistory.value?.length) return 0;
  const wins = matchHistory.value.filter((m) => m.outcome === 'won').length;
  return ((wins / matchHistory.value.length) * 100).toFixed(0);
});

// Sync filters from URL on load
function setFiltersFromQuery() {
  const p = route.query.platform;
  if (p === 'valve') selectedPlatform.value = 'Valve';
  else if (p === 'faceit') selectedPlatform.value = 'Faceit';
  else if (p === 'renown') selectedPlatform.value = 'Renown';
  else if (p === 'gamers_club') selectedPlatform.value = 'gamers_club';
  else selectedPlatform.value = '';
}

function updateFilterQuery() {
  const nextQuery = { ...route.query };
  if (selectedPlatform.value) {
    nextQuery.platform = platformFilterToApi(selectedPlatform.value);
  } else {
    delete nextQuery.platform;
  }
  if (selectedMap.value?.name) {
    nextQuery.map = selectedMap.value.name.toLowerCase();
  } else {
    delete nextQuery.map;
  }
  router.replace({ query: nextQuery });
}

function fetchMatches() {
  loadingMatches.value = true;
  axios.get(`/coach/players/${user.value?.steam_id}/recent_match_stats`, {
    params: {
      match_type: selectedMatchType.value.toLowerCase(),
      platform: platformFilterToApi(selectedPlatform.value),
      map_name: selectedMap.value?.name?.toLowerCase() || '',
      page: currentPage.value,
    },
  }).then((response) => {
    matchHistory.value = response.data.matches;
    totalPageCount.value = response.data.pagination.total_pages;
    loadingMatches.value = false;
  }).catch(() => {
    matchHistory.value = [];
    loadingMatches.value = false;
  });
}

function retrieveMatches() {
  gettingMatches.value = true;
  let dismissAfter = SHORT_DISMISS_MS;
  axios.post('/matches/retrieve_my_matches')
    .then((response) => {
      // Only here: the backend rejects a setup that cannot return matches, and
      // the thumbs-up used to go up either way. A 200 can still carry a warning
      // about a platform we cannot fetch, which is what the message reads off.
      gettingMatchesSuccess.value = true;
      const message = retrievalStartedMessage(response);
      dismissAfter = messageDismissDelay(message);
      store.commit('showMessage', message);
    })
    .catch((error) => {
      const message = retrievalFailedMessage(error);
      dismissAfter = messageDismissDelay(message);
      store.commit('showMessage', message);
    })
    .finally(() => {
      gettingMatches.value = false;
      setTimeout(() => {
        gettingMatchesSuccess.value = false;
        store.commit('hideMessage');
      }, dismissAfter);
    });
}

function onPlatformChange(value) {
  if (selectedPlatform.value !== value) {
    selectedMatchType.value = '';
    selectedMap.value = null;
  }
  selectedPlatform.value = value;
  currentPage.value = 1;
  updateFilterQuery();
  fetchMatches();
}

function onMatchTypeChange(value) {
  selectedMatchType.value = value;
  selectedMap.value = null;
  currentPage.value = 1;
  fetchMatches();
}

function onMapChange(value) {
  selectedMap.value = value;
  currentPage.value = 1;
  updateFilterQuery();
  fetchMatches();
}

function changePage(page) {
  currentPage.value = page;
  fetchMatches();
}

// Init
setFiltersFromQuery();
fetchMatches();
</script>
